<template>
  <article class="products-card">
    <div class="products-card__media">
      <img
        :src="displayImageUrl"
        :alt="product.name"
        loading="lazy"
        @error="handleImageError"
      />
    </div>
    <div class="products-card__body">
      <span class="products-card__type">{{ product.type || 'BioEdge' }}</span>
      <h3 class="products-card__title">{{ product.name }}</h3>
    </div>
    <div class="products-card__footer">
      <span class="products-card__price-pill">R{{ formatPriceValue(product.price) }}</span>
      <div class="products-card__actions">
        <button
          type="button"
          class="products-card__button products-card__button--ghost"
          @click.stop="toggleDetail"
        >
          {{ detailVisible ? 'Close' : 'View' }}
        </button>
        <button
          type="button"
          class="products-card__button products-card__button--primary"
          @click.stop="$emit('add-to-cart', product)"
        >
          Add to Cart
        </button>
      </div>
    </div>
    <transition name="detail-fade">
      <div
        v-if="detailVisible"
        class="products-card__detail"
      >
        <button
          type="button"
          class="products-card__detail-close"
          @click="detailVisible = false"
          aria-label="Close product details"
        >
          X
        </button>
        <h4>{{ product.name }}</h4>
        <p>{{ product.description || 'Detailed information coming soon.' }}</p>
      </div>
    </transition>
  </article>
</template>

<script>
import { getProductImageUrl } from '../utils/productImageUrl'

export default {
  name: 'ProductCard',
  props: {
    product: {
      type: Object,
      required: true
    },
    formatPrice: {
      type: Function,
      default: value => {
        if (typeof value === 'number') {
          return value.toLocaleString('en-ZA')
        }
        const numeric = parseFloat(String(value).replace(/[^\d.]/g, ''))
        return Number.isNaN(numeric) ? '0' : numeric.toLocaleString('en-ZA')
      }
    }
  },
  emits: ['add-to-cart'],
  data() {
    return {
      detailVisible: false
    }
  },
  computed: {
    displayImageUrl() {
      return (
        getProductImageUrl(this.product) ||
        'https://via.placeholder.com/320x320/52CD36/FFFFFF?text=Bio+Edge'
      )
    }
  },
  methods: {
    toggleDetail() {
      this.detailVisible = !this.detailVisible
    },
    handleImageError(event) {
      event.target.src = 'https://via.placeholder.com/320x320/52CD36/FFFFFF?text=Bio+Edge'
    },
    formatPriceValue(value) {
      return this.formatPrice(value)
    }
  }
}
</script>

<style scoped>
.products-card {
  position: relative;
  background: #ffffff;
  border-radius: 28px;
  padding: 1.75rem 1.5rem 1.6rem;
  box-shadow: 0 24px 48px rgba(18, 48, 22, 0.15);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.products-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 36px 70px rgba(18, 48, 22, 0.22);
}

.products-card__media {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.products-card__media img {
  width: clamp(8rem, 14vw, 10rem);
  height: clamp(8rem, 14vw, 10rem);
  object-fit: contain;
  filter: drop-shadow(0 18px 32px rgba(0, 0, 0, 0.18));
}

.products-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
}

.products-card__type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 0.35rem 1rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  background: rgba(82, 205, 54, 0.12);
  border: 1px solid rgba(82, 205, 54, 0.25);
  color: #1f3016;
  margin: 0 auto;
}

.products-card__title {
  font-family: 'Montserrat', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.3;
  color: #0f1d12;
}

.products-card__footer {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;
}

.products-card__price-pill {
  display: inline-flex;
  align-self: center;
  align-items: center;
  justify-content: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f1d12;
  background: #ffffff;
  padding: 0.5rem 1.4rem;
  border-radius: 9999px;
  box-shadow: 0 18px 36px rgba(15, 29, 18, 0.12);
}

.products-card__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.products-card__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1rem;
  border-radius: 14px;
  font-family: 'Montserrat', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  border: none;
  cursor: pointer;
}

.products-card__button--ghost {
  background: rgba(15, 29, 18, 0.04);
  border: 1px solid rgba(82, 205, 54, 0.3);
  color: #0f1d12;
}

.products-card__button--ghost:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(31, 48, 22, 0.18);
  background: rgba(15, 29, 18, 0.08);
}

.products-card__button--primary {
  background: linear-gradient(135deg, #52cd36, #3da026);
  color: #ffffff;
}

.products-card__button--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 42px rgba(82, 205, 54, 0.32);
}

.products-card__detail {
  position: absolute;
  inset: 0;
  background: rgba(15, 29, 18, 0.92);
  color: #ffffff;
  border-radius: 28px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.products-card__detail h4 {
  font-size: 1.2rem;
  font-weight: 700;
}

.products-card__detail p {
  font-size: 0.95rem;
  line-height: 1.6;
  max-width: 18rem;
}

.products-card__detail-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
}

.detail-fade-enter-active,
.detail-fade-leave-active {
  transition: opacity 0.2s ease;
}

.detail-fade-enter-from,
.detail-fade-leave-to {
  opacity: 0;
}
</style>
